import { useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { useLocationBlocker } from "@/hooks/useLocationBlocker"
import { useNavigationBlocker } from "@/hooks/useNavigationBlocker"
import { toast } from "@/hooks/useToast"
import { Header } from "@/layouts/header"
import { CreateEditTreatmentPlanTemplatesForm } from "@/modules/admin/treatment/components/treatment-form"
import { useTreatmentParam } from "@/modules/admin/treatment/hooks/useTreatmentParam"
import { TreatmentForm, treatmentFormSchema } from "@/modules/admin/treatment/schema/treatment-form-schema"
import {
  CreatePersonalTreatmentPlanTemplateInput,
  TreatmentPlanTemplateLanguageInput,
  useCreatePersonalTreatmentPlanTemplateMutation$data,
} from "@/modules/admin/treatment/services/__generated__/useCreatePersonalTreatmentPlanTemplateMutation.graphql"
import { UpdatePersonalTreatmentPlanTemplateInput } from "@/modules/admin/treatment/services/__generated__/useUpdatePersonalTreatmentPlanTemplateMutation.graphql"
import { useCreatePersonalTreatmentPlanTemplateMutationHook } from "@/modules/admin/treatment/services/useCreatePersonalTreatmentPlanTemplateMutation"
import { useGetTreamentPlanTemplateByIdQuery } from "@/modules/admin/treatment/services/useTreamentById"
import { useUpdatePersonalTreatmentPlanTemplateMutationHook } from "@/modules/admin/treatment/services/useUpdatePersonalTreatmentPlanTemplateMutation"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { getIsDirty, useDrawerStore } from "@/stores/useDrawerStore"
import { LanguageValues } from "@/types/__generated__/union-types"

const TreatmentPlanTemplatesCreateEdit = () => {
  const { t } = useTranslation()
  const templateId = useTreatmentParam()
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const navigate = useNavigate()
  const [createTemplate, isCreating] = useCreatePersonalTreatmentPlanTemplateMutationHook()
  const [updateTemplate, isUpdating] = useUpdatePersonalTreatmentPlanTemplateMutationHook()

  const templateData = useGetTreamentPlanTemplateByIdQuery(templateId ?? "", !templateId)

  const updateTemplateData: TreatmentForm | null = templateData.treatmentPlanTemplateById
    ? ({
        id: templateData.treatmentPlanTemplateById.id,
        name: templateData.treatmentPlanTemplateById.name,
        countries: [...templateData.treatmentPlanTemplateById.countries],
        diagnosisCodes: templateData.treatmentPlanTemplateById.icdDiagnoses
          ? [...templateData.treatmentPlanTemplateById.icdDiagnoses]
          : [],
        ...LanguageValues.reduce((acc, lang) => {
          const languageData = templateData.treatmentPlanTemplateById?.languages.find((e) => e.language === lang)
          const lowerLanguage = lang.toLowerCase()

          return {
            ...acc,
            [`${lowerLanguage}Title`]: languageData?.title ?? "",
            [`${lowerLanguage}Instruction`]: languageData?.followUpInstructions ?? "",
            [`${lowerLanguage}Synonyms`]: languageData?.synonym ?? "",
            [`${lowerLanguage}Recommended`]: languageData?.recommendedTreatment ?? "",
          }
        }, {}),
      } as TreatmentForm)
    : null

  const form = useForm<TreatmentForm>({
    resolver: zodResolver(treatmentFormSchema),
    defaultValues: updateTemplateData ?? {},
  })

  const { handleSubmit } = form

  const onSubmit = (data: TreatmentForm) => {
    closeDrawer()
    const languages: TreatmentPlanTemplateLanguageInput[] = [
      {
        language: "ENGLISH",
        title: data.englishTitle,
        synonym: data.englishSynonyms,
        followUpInstructions: data.englishInstruction,
        recommendedTreatment: data.englishRecommended,
      },
      {
        language: "GERMAN",
        title: data.germanTitle,
        synonym: data.germanSynonyms,
        followUpInstructions: data.germanInstruction,
        recommendedTreatment: data.germanRecommended,
      },
      {
        language: "FRENCH",
        title: data.frenchTitle,
        synonym: data.frenchSynonyms,
        followUpInstructions: data.frenchInstruction,
        recommendedTreatment: data.frenchRecommended,
      },
      {
        language: "ITALIAN",
        title: data.italianTitle,
        synonym: data.italianSynonyms,
        followUpInstructions: data.italianInstruction,
        recommendedTreatment: data.italianRecommended,
      },
    ]
    const payloadData = {
      name: data.name,
      countries: data.countries,
      diagnosisCodes: data.diagnosisCodes?.map((item) => item.diagnosisCode) ?? [],
      languages,
    }

    if (templateId) {
      const payloadUpdate: UpdatePersonalTreatmentPlanTemplateInput = {
        id: templateId,
        name: payloadData.name,
        countries: payloadData.countries,
        diagnosisCodes: payloadData.diagnosisCodes,
        languages: payloadData.languages,
      }
      updateTemplate({
        variables: {
          input: payloadUpdate,
        },
        onCompleted: (response) => {
          const errMessage = response.updatePersonalTreatmentPlanTemplate?.errors?.[0]?.message
          handleCreateUpdateTemplateResponse(errMessage)
        },
      })
    } else {
      const payloadCreate: CreatePersonalTreatmentPlanTemplateInput = {
        name: payloadData.name,
        countries: payloadData.countries,
        diagnosisCodes: payloadData.diagnosisCodes,
        languages: payloadData.languages,
      }
      createTemplate({
        variables: {
          input: payloadCreate,
        },
        onCompleted: (response: useCreatePersonalTreatmentPlanTemplateMutation$data) => {
          const errMessage = response.createPersonalTreatmentPlanTemplate?.errors?.[0]?.message
          handleCreateUpdateTemplateResponse(errMessage)
        },
      })
    }
  }

  const goBack = () => {
    navigate("/admin/treatment-plan-templates")
  }

  const handleCreateUpdateTemplateResponse = (errorMessage: string | undefined) => {
    if (errorMessage) {
      toast({
        description: errorMessage,
      })
    } else {
      goBack()
    }
  }

  const handleDiscard = () => {
    confirm({
      title: t("common.confirmation.discardUnsavedChanges"),
      content: t("common.confirmation.unsavedChangesWillBeLostPermanently"),
      cancelText: t("common.confirmation.keepEditing"),
      confirmText: t("common.confirmation.discard"),
      onConfirm: () => {
        goBack()
        cancel()
      },
    })
  }

  useNavigationBlocker(getIsDirty)

  const blocker = useLocationBlocker()

  useEffect(() => {
    if (blocker.state === "blocked") {
      if (useDrawerStore.getState().DrawerContent && useDrawerStore.getState().isDirty) {
        confirm({
          onConfirm: () => {
            if (useDrawerStore.getState().onDrawerClose) {
              useDrawerStore.getState().onDrawerClose?.()
            } else {
              useDrawerStore.getState().closeDrawer()
            }
            useDrawerStore.getState().setDirty(false)
            blocker.proceed()
            cancel()
          },
        })
      } else {
        useDrawerStore.getState().closeDrawer()
        blocker.proceed()
      }
    }
  }, [blocker, cancel, confirm])

  return (
    <>
      <Header
        title={
          templateId ? t("admin.treatment.treatmentEdit.pageTitle") : t("admin.treatment.treatmentCreate.pageTitle")
        }>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="secondary" data-testid="treatment-discard-button" onClick={handleDiscard}>
            {t("common.confirmation.discard")}
          </Button>
          <Button
            isLoading={isCreating || isUpdating}
            data-testid="treatment-save-button"
            onClick={handleSubmit(onSubmit)}>
            {t("common.button.save")}
          </Button>
        </div>
      </Header>
      <FormProvider {...form}>
        <CreateEditTreatmentPlanTemplatesForm />
      </FormProvider>
    </>
  )
}

export { TreatmentPlanTemplatesCreateEdit }
